import React from 'react';

import { FormControlLabel } from '@mui/material';

import Switch from '@mui/material/Switch';

import { ColumnActions } from 'components/Grid/types';

import { useStore } from 'utils/hooks/store';

import API from 'utils/api';
import Logger from 'utils/logger';

import { colors } from 'theme/colors';

export type AuditTemplateActiveColumnProps = {
  row: { uuid: string; isActive?: boolean; settings?: { isActive: boolean } };
  actions: ColumnActions;
};

// The audits templates-list serializer lifts `isActive` out of `settings`; the PATCH endpoint keeps it nested.
const AuditTemplateActiveColumn: React.FC<AuditTemplateActiveColumnProps> = ({ row, actions }) => {
  const { notification } = useStore();
  const { uuid } = row;
  const isActive = row.isActive ?? row.settings?.isActive ?? false;

  // Two fast clicks would both PATCH off the same stale `isActive`, undoing each other.
  const pending = React.useRef(false);

  const onToggleActive = async (): Promise<void> => {
    if (pending.current) {
      return;
    }
    pending.current = true;

    try {
      await API.entities.updateEntityForm('audits', uuid, { settings: { isActive: !isActive } })();
      actions.refreshData();
      notification.enqueueSuccessSnackbar('Template has been updated');
    } catch (e) {
      notification.enqueueErrorSnackbar('Template cannot be updated right now.');
      Logger.error(`Invalid update template API response. Template ID: ${uuid}`, e);
    } finally {
      pending.current = false;
    }
  };

  const onClick = (event: React.SyntheticEvent): void => {
    onToggleActive();
    event.stopPropagation();
  };

  return (
    <FormControlLabel
      sx={{
        gap: '12px',
        '& .MuiFormControlLabel-label': {
          color: colors.text.primary,
          fontSize: '14px',
        },
      }}
      control={<Switch size="small" onClick={onClick} checked={isActive} aria-label="Active switch" />}
      label={isActive ? 'Active' : 'Inactive'}
    />
  );
};

export default AuditTemplateActiveColumn;
